/**
 * Partner payments (BE-3e) — money between this typing centre and the partner centres it delegates work to.
 *
 * Route: /accounting/partner-payments
 * Pending: created from the partner's side (wallet top-up, refund, a delegated item paid by our card); finance posts
 * each one by naming its own account (or card). Posted: history. Partners: balance per partner from the ledger.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Tabs, Tab, Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, ListSubheader, TablePagination, CircularProgress, IconButton, Tooltip, Link, Card, CardContent,
  Stack, InputAdornment, useTheme, useMediaQuery,
} from '@mui/material';
import { PlaylistAddCheck as PostIcon, Refresh as RefreshIcon, Search as SearchIcon } from '@mui/icons-material';
import { SummaryCardGrid, DataTable, StatusChip, CurrencyText, JournalEntryLink } from '../../components/accounting';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { formatCurrency, formatDate } from '../../utils/accounting/formatters';
import { can } from '../../utils/roleUtils';
import ApiService from '../../services/api.service';
import { workOrderMoneyService } from '../../services/accounting/workOrderMoney.service';
import { creditCardService } from '../../services/accounting/creditCard.service';
import { serverMessage, unwrapList, unwrapPage } from '../../utils/apiConvert';
import { cardLabel } from './CreditCards';
import { invoiceMoneyService } from '../../services/accounting/invoiceMoney.service';
import { COMPONENT_LABEL } from '../CreditNoteDialog';

const KIND_LABEL = { TOP_UP: 'Wallet top-up', CUSTOMER_REFUND: 'Customer refund', CARD_ITEM: 'Item paid by card' };
const DIR = {
  PAID_TO_PARTNER: { label: 'Paid to partner', tone: 'warning' },
  RECEIVED_FROM_PARTNER: { label: 'Received from partner', tone: 'success' },
};

const sessionUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
};

const sum = (rows, direction) => rows.filter((p) => p.direction === direction).reduce((t, p) => t + Number(p.amount || 0), 0);

const PartnerPayments = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const canWrite = can(sessionUser(), 'finance', 'write');
  const [tab, setTab] = useState(0);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [pending, setPending] = useState([]);
  const [posted, setPosted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [direction, setDirection] = useState('');
  const [balances, setBalances] = useState({ content: [], totalElements: 0 });
  const [balPage, setBalPage] = useState(0);
  const [balLoading, setBalLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [cards, setCards] = useState([]);
  const [posting, setPosting] = useState(null);
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [prompts, setPrompts] = useState({ content: [], totalElements: 0 });

  const loadPayments = useCallback(async () => {
    setLoading(true);
    try {
      const [p, d] = await Promise.all([
        workOrderMoneyService.getPartnerPayments('PENDING'),
        workOrderMoneyService.getPartnerPayments('POSTED'),
      ]);
      setPending(p);
      setPosted(d);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load partner payments'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBalances = useCallback(async () => {
    setBalLoading(true);
    try {
      setBalances(unwrapPage(await ApiService.get('/api/partner-payments/balances', { params: { page: balPage, size: 20 } })));
    } catch (err) {
      setError(serverMessage(err, 'Failed to load partner balances'));
    } finally {
      setBalLoading(false);
    }
  }, [balPage]);

  const promptRows = prompts.content.flatMap((note) => (note.lines || []).map((l) => ({ ...l, note })));
  const promptNotes = prompts.content.filter((note) => (note.lines || []).length > 0).length;

  const loadPrompts = useCallback(() => {
    invoiceMoneyService.getAllPartnerPrompts(0, 20).then(setPrompts).catch(() => {});
  }, []);

  useEffect(() => { loadPayments(); loadPrompts(); }, [loadPayments, loadPrompts]);
  useEffect(() => { if (tab === 2) loadBalances(); }, [tab, loadBalances]);
  useEffect(() => {
    ApiService.getTcBankAccounts().then(unwrapList).then((r) => setAccounts(r.items)).catch(() => {});
    creditCardService.getCards().then(setCards).catch(() => {});
  }, []);

  const refresh = () => { loadPayments(); loadPrompts(); if (tab === 2) loadBalances(); };
  const payable = accounts.filter((a) => a.isActive !== false && ['OPERATIONS', 'CASH', 'E_WALLET'].includes(a.accountType));
  const activeCards = cards.filter((c) => c.active !== false);
  const accountName = (p) => {
    if (p.cardId) { const c = cards.find((x) => x.id === p.cardId); return c ? cardLabel(c) : 'Credit card'; }
    const a = accounts.find((x) => x.id === p.accountId);
    return a ? [a.bankName, a.accountName].filter(Boolean).join(' — ') : '—';
  };

  const term = search.trim().toLowerCase();
  const matches = (name) => !term || (name || '').toLowerCase().includes(term);
  const rows = (tab === 0 ? pending : posted).filter((p) => matches(p.partnerName) && (!direction || p.direction === direction));
  const partners = balances.content.filter((b) => matches(b.partnerName));
  const isPending = tab === 0;

  const summaryCards = [
    { label: 'Waiting to Post', value: pending.length, suffix: pending.length === 1 ? 'payment' : 'payments', color: 'warning' },
    { label: 'Pending Amount', value: pending.reduce((t, p) => t + Number(p.amount || 0), 0), color: 'primary' },
    { label: 'Paid to Partners', value: sum(posted, 'PAID_TO_PARTNER'), color: 'error' },
    { label: 'Received from Partners', value: sum(posted, 'RECEIVED_FROM_PARTNER'), color: 'success' },
  ];

  const submitPost = async () => {
    const [kind, id] = target.split(':');
    setBusy(true);
    setError(null);
    try {
      await workOrderMoneyService.postPartnerPayment(posting.id, kind === 'CARD' ? { cardId: id } : { accountId: id });
      setSnackbar('Partner payment posted');
      setPosting(null);
      await loadPayments();
    } catch (err) {
      setError(serverMessage(err, 'Failed to post the payment'));
      setPosting(null);
    } finally {
      setBusy(false);
    }
  };

  const postButton = (p) => isPending && canWrite && (
    <Tooltip title="Post — choose the account">
      <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); setTarget(''); setPosting(p); }}><PostIcon sx={{ fontSize: 20 }} /></IconButton>
    </Tooltip>
  );
  const netText = (net) => `${formatCurrency(Math.abs(Number(net)))}${Number(net) > 0 ? ' you owe' : Number(net) < 0 ? ' in your favour' : ''}`;
  const netColor = (net) => (Number(net) > 0 ? 'error.main' : Number(net) < 0 ? 'success.main' : 'text.primary');
  const empty = (text) => (
    <Paper variant="outlined" sx={{ py: 4, textAlign: 'center' }}>
      <Typography color="text.secondary" sx={{ fontSize: fs.sm }}>{text}</Typography>
    </Paper>
  );
  const spinner = <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  const PAYMENT_COLUMNS = [
    { key: 'occurredOn', label: 'Date', width: '11%', render: (p) => <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{formatDate(p.occurredOn)}</Box> },
    { key: 'partnerName', label: 'Partner', width: '18%', render: (p) => (
      <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold }}>{p.partnerName}</Typography>
    ) },
    { key: 'kind', label: 'What', width: '18%', render: (p) => (
      <Box>
        {KIND_LABEL[p.kind] || p.kind}
        {p.workOrderId && (
          <Box>
            <Link component="button" underline="hover" sx={{ fontSize: fs.xs }} onClick={() => navigate(`/work-orders/${p.workOrderId}`)}>Open work order</Link>
          </Box>
        )}
      </Box>
    ) },
    { key: 'direction', label: 'Direction', width: '15%', render: (p) => <StatusChip tone={DIR[p.direction]?.tone} label={DIR[p.direction]?.label || p.direction} /> },
    { key: 'amount', label: 'Amount', width: '12%', align: 'right', render: (p) => <CurrencyText amount={p.amount} bold /> },
    { key: 'ref', label: isPending ? 'Partner Reference' : 'Posted To', width: '18%', render: (p) => (isPending ? (p.partnerReference || '—') : (
      <Box display="flex" alignItems="center" gap={0.5}>
        {accountName(p)}
        <JournalEntryLink id={p.journalEntryId} />
      </Box>
    )) },
    ...(isPending ? [{ key: 'actions', label: '', width: '8%', align: 'right', render: postButton }] : []),
  ];

  const BALANCE_COLUMNS = [
    { key: 'partnerName', label: 'Partner', width: '34%', render: (b) => (
      <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold }}>{b.partnerName}</Typography>
    ) },
    { key: 'payable', label: 'You Owe', width: '20%', align: 'right', render: (b) => <CurrencyText amount={b.payable} /> },
    { key: 'advances', label: 'Prepaid to Partner', width: '20%', align: 'right', render: (b) => <CurrencyText amount={b.advances} /> },
    { key: 'net', label: 'Net', width: '26%', align: 'right', render: (b) => (
      <Typography sx={{ fontSize: fs.xs, fontWeight: fw.bold, color: netColor(b.net) }}>{netText(b.net)}</Typography>
    ) },
  ];

  const paymentList = isMobile ? (
    <Stack spacing={1.5}>
      {loading ? spinner : rows.length === 0 ? empty('No partner payments found') : rows.map((p) => (
        <Card key={p.id} variant="outlined">
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Box>
                <Typography fontWeight={600} color="primary" sx={{ fontSize: '0.95rem' }}>{p.partnerName}</Typography>
                <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>{KIND_LABEL[p.kind] || p.kind}</Typography>
              </Box>
              <StatusChip tone={DIR[p.direction]?.tone} label={DIR[p.direction]?.label || p.direction} />
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{formatDate(p.occurredOn)}</Typography>
                <Typography variant="caption" color="text.secondary">{isPending ? (p.partnerReference || '') : accountName(p)}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography fontWeight={700} color="primary" sx={{ fontSize: '1rem' }}>{formatCurrency(p.amount)}</Typography>
                <JournalEntryLink id={p.journalEntryId} />
                {postButton(p)}
              </Box>
            </Box>
            {p.workOrderId && (
              <Link component="button" underline="hover" variant="caption" onClick={() => navigate(`/work-orders/${p.workOrderId}`)}>Open work order</Link>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  ) : (
    <DataTable columns={PAYMENT_COLUMNS} data={rows} loading={loading} emptyMessage="No partner payments found" />
  );

  const balanceList = isMobile ? (
    <Stack spacing={1.5}>
      {balLoading ? spinner : partners.length === 0 ? empty('No partner balances yet') : partners.map((b) => (
        <Card key={b.partnerTypingCenterId} variant="outlined">
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Typography fontWeight={600} color="primary" sx={{ fontSize: '0.95rem', mb: 1 }}>{b.partnerName}</Typography>
            <Box display="flex" justifyContent="space-between" alignItems="flex-end">
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">You owe {formatCurrency(b.payable)}</Typography>
                <Typography variant="caption" color="text.secondary">Prepaid {formatCurrency(b.advances)}</Typography>
              </Box>
              <Typography fontWeight={700} sx={{ fontSize: '1rem' }} color={netColor(b.net)}>{netText(b.net)}</Typography>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Stack>
  ) : (
    <DataTable columns={BALANCE_COLUMNS} data={partners} loading={balLoading} pagination={false} emptyMessage="No partner balances yet" />
  );

  const tabSx = { textTransform: 'none', fontSize: fs.sm };

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={600} color="secondary.main">Partner Payments</Typography>
        <Tooltip title="Refresh"><IconButton size="small" onClick={refresh}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
      </Box>

      <SummaryCardGrid cards={summaryCards} loading={loading} sx={{ mb: 2 }} />

      {promptRows.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderLeft: '4px solid', borderLeftColor: 'warning.main' }}>
          <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, mb: 0.5 }}>Partner credit notes to pass on ({promptNotes})</Typography>
          <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mb: 1.5 }}>
            A partner credited items you billed to your customer. Issue your own credit note from the invoice if you refund the customer.
          </Typography>
          <Stack spacing={1}>
            {promptRows.map((pr) => (
              <Box key={`${pr.note.partnerCreditNoteId}-${pr.invoiceLineItemId}-${pr.component}`} display="flex" justifyContent="space-between"
                alignItems="center" gap={1} flexWrap="wrap">
                <Box>
                  <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold }}>
                    {pr.note.partnerName} · {pr.note.partnerCreditNoteNumber} · {formatCurrency(pr.amount)} {COMPONENT_LABEL[pr.component] || pr.component}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {pr.serviceName} — billed to {pr.customerName} on {pr.invoiceNumber} · {formatDate(pr.note.partnerCreditNoteDate)}
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" sx={{ textTransform: 'none' }} onClick={() => navigate(`/invoices/${pr.invoiceId}`)}>Open invoice</Button>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, width: 0, minWidth: '100%' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label={`Pending (${pending.length})`} sx={tabSx} />
          <Tab label="Posted" sx={tabSx} />
          <Tab label="Partners" sx={tabSx} />
        </Tabs>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Search partner..." value={search} sx={{ flex: '1 1 240px', maxWidth: 360 }}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        {tab !== 2 && (
          <TextField select size="small" label="Direction" value={direction} sx={{ minWidth: 200 }} onChange={(e) => setDirection(e.target.value)}>
            <MenuItem value="">Both directions</MenuItem>
            <MenuItem value="PAID_TO_PARTNER">Paid to partner</MenuItem>
            <MenuItem value="RECEIVED_FROM_PARTNER">Received from partner</MenuItem>
          </TextField>
        )}
      </Box>

      {tab === 2 ? (
        <>
          {balanceList}
          {balances.totalElements > 0 && (
            <TablePagination component="div" count={balances.totalElements} page={balPage} rowsPerPage={20}
              onPageChange={(_, p) => setBalPage(p)} rowsPerPageOptions={[20]}
              sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: fs.xs } }} />
          )}
        </>
      ) : paymentList}

      <Dialog open={!!posting} onClose={() => setPosting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Post Partner Payment</DialogTitle>
        <DialogContent>
          {posting && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {formatCurrency(posting.amount)} — {KIND_LABEL[posting.kind] || posting.kind},{' '}
              {posting.direction === 'PAID_TO_PARTNER' ? 'paid to' : 'received from'} {posting.partnerName}
            </Typography>
          )}
          <TextField select fullWidth size="small" label={posting?.direction === 'PAID_TO_PARTNER' ? 'Paid from *' : 'Received into *'}
            value={target} onChange={(e) => setTarget(e.target.value)}>
            <ListSubheader>Accounts</ListSubheader>
            {payable.map((a) => (
              <MenuItem key={a.id} value={`ACCOUNT:${a.id}`}>
                {[a.bankName, a.accountName].filter(Boolean).join(' - ')} {a.accountType !== 'OPERATIONS' ? `(${a.accountType === 'CASH' ? 'cash' : 'e-wallet'})` : ''}
              </MenuItem>
            ))}
            {posting?.direction === 'PAID_TO_PARTNER' && activeCards.length > 0 && <ListSubheader>Credit cards</ListSubheader>}
            {posting?.direction === 'PAID_TO_PARTNER' && activeCards.map((c) => (
              <MenuItem key={c.id} value={`CARD:${c.id}`}>{cardLabel(c)}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPosting(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy || !target} onClick={submitPost}>Post</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)} message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default PartnerPayments;
